import React from 'react';
import {
  Users,
  ClipboardList,
  Calendar,
  AlertTriangle,
  TrendingUp,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  ChevronRight,
} from 'lucide-react';
import { useCompany, useWorkOrders, useVisits } from '../lib/hooks';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';

const Dashboard = () => {
  const { company, loading: companyLoading } = useCompany();
  const { workOrders } = useWorkOrders();
  const { visits } = useVisits();

  if (companyLoading) {
    return (
      <div className="p-8">
        <div className="space-y-4">
          <Skeleton className="h-8 w-[250px]" />
          <Skeleton className="h-4 w-[350px]" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="relative overflow-hidden">
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-[140px]" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-[100px]" />
                  <Skeleton className="h-4 w-[80px] mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: "Active Customers",
      value: "1,234",
      change: "+5.2%",
      trend: "up",
      icon: Users,
      color: "blue"
    },
    {
      title: "Open Work Orders",
      value: "42",
      change: "-2.1%",
      trend: "down",
      icon: ClipboardList,
      color: "green"
    },
    {
      title: "Today's Visits",
      value: "18",
      change: "+12.5%",
      trend: "up",
      icon: Calendar,
      color: "purple"
    },
    {
      title: "Urgent Tasks",
      value: "3",
      change: "0%",
      trend: "neutral",
      icon: AlertTriangle,
      color: "red"
    }
  ];

  return (
    <div className="p-8 space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Welcome back, {company?.name}</h1>
        <p className="text-muted-foreground">
          Here's what's happening with your service business today
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <Card key={stat.title} className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <div className={`text-${stat.color}-500`}>
                <stat.icon className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="flex items-center text-sm text-muted-foreground">
                {stat.trend === "up" ? (
                  <ArrowUpRight className="mr-1 h-4 w-4 text-green-500" />
                ) : stat.trend === "down" ? (
                  <ArrowDownRight className="mr-1 h-4 w-4 text-red-500" />
                ) : (
                  <div className="mr-1 h-4 w-4" />
                )}
                <span className={
                  stat.trend === "up" 
                    ? "text-green-500" 
                    : stat.trend === "down" 
                      ? "text-red-500" 
                      : ""
                }>
                  {stat.change}
                </span>
                <span className="ml-1">from last month</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-7">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Recent Work Orders</CardTitle>
            <CardDescription>
              Your latest service requests and their status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {workOrders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback>
                      {order.customer?.first_name?.[0]}
                      {order.customer?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="ml-4 space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {order.customer?.first_name} {order.customer?.last_name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {order.service_type}
                    </p>
                  </div>
                  <div className="ml-auto font-medium">
                    <HoverCard>
                      <HoverCardTrigger>
                        <Button variant="ghost" size="sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold
                            ${order.status === 'completed' ? 'bg-green-100 text-green-800' : 
                              order.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'}`}>
                            {order.status}
                          </span>
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent>
                        <div className="space-y-2">
                          <p className="text-sm font-medium">{order.description}</p>
                          <p className="text-xs text-muted-foreground">
                            Created on {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            {workOrders.length > 5 && (
              <div className="mt-4 flex justify-center">
                <Button variant="outline" className="w-full">
                  View all work orders
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>Upcoming Visits</CardTitle>
            <CardDescription>
              Scheduled service visits for today
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {visits.slice(0, 5).map((visit) => (
                <div key={visit.id} className="flex items-center">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50">
                    <Clock className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-4 space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {visit.time}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {visit.customer}
                    </p>
                  </div>
                  <div className="ml-auto">
                    <Button variant="ghost" size="sm">
                      View details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {visits.length > 5 && (
              <div className="mt-4 flex justify-center">
                <Button variant="outline" className="w-full">
                  View all visits
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;